import { Type } from 'class-transformer';
import type { SMSTemplateSchema } from '@stuut/api-sdk/models';
import { CustomerSegment } from './customer_segment';
import { EmailTemplate } from './email_template';
import { Model } from './model';
import { InternalRole, ExternalRole } from './contact';
import { EmailSender } from './email_sender';
import type dayjs from 'dayjs';
import { TransformStringToDayJS } from './utils';

export class OutreachWorkflow extends Model {
  id: string;

  email_sender_id: string | null;

  @Type(() => EmailSender)
  email_sender: EmailSender | null;

  is_active: boolean;

  name: string;

  timezone: string;

  active_days: number[];

  has_partner_filters: boolean;

  is_call_on_weekend_enabled: boolean;

  outreach_window_start: string;

  outreach_window_end: string;

  segment_id: string | null;

  @Type(() => CustomerSegment)
  segment: CustomerSegment | string | null;

  test_email: string | null;

  test_phone_number: string | null;

  use_recipient_timezone_for_calls: boolean;

  @Type(() => OutreachWorkflowStage)
  stages: OutreachWorkflowStage[];

  @Type(() => AccountStatementSchedule)
  statement_schedule: AccountStatementSchedule | null;
}

export type StatementFrequency = 'weekly' | 'biweekly' | 'monthly';

export class AccountStatementSchedule extends Model {
  id: string;
  is_active: boolean;
  frequency: StatementFrequency;
  day_of_week: number | null;
  day_of_month: number | null;
  week_of_month: number | null;
  biweekly_anchor_date: string | null;
  include_credit_memos: boolean;
  include_disputed_items: boolean;
  auto_send: boolean;
  attach_xls: boolean;

  @Type(() => EmailTemplate)
  email_template: EmailTemplate | null;

  cc: string[];
  bcc: string[];
  cc_secondary_contacts: boolean;
  external_roles_to_cc: ExternalRole[];
  internal_roles_to_cc: InternalRole[];
  workflow_id: string;
}

export type OutreachWorkflowActionType = 'email' | 'call' | 'sms';

export type OutreachWorkflowEventType =
  | 'invoice.issued_at'
  | 'invoice.due_date'
  | 'invoice.drafted_at';

export type OutreachWorkflowTimeTriggerType = 'after' | 'before' | 'on';

export enum OutreachWorkflowStagePartnerEvalStatus {
  RUNNING = 'running', // blue?
  SKIPPED = 'skipped', // gray
  FAILED = 'failed', // red
  RUN_SCHEDULED = 'run_scheduled', // green
  RUN_ON_PARENT = 'run_on_parent', // green
}

export class OutreachWorkflowStage extends Model {
  id: string;

  name: string;

  action: OutreachWorkflowActionType;

  auto_send: boolean;

  bcc: string[];

  cc: string[];

  cc_secondary_contacts: boolean;

  custom_recipient_emails: string[];

  internal_roles_to_cc: InternalRole[];

  is_always_run_enabled: boolean;

  external_roles_to_cc: ExternalRole[];

  @Type(() => EmailTemplate)
  email_template: EmailTemplate | null;

  sms_template: SMSTemplateSchema | null;

  event: OutreachWorkflowEventType;

  num_days: number | null;

  outreach_window_start: string | null;

  outreach_window_end: string | null;

  time_trigger: OutreachWorkflowTimeTriggerType;

  recurrence_interval_days: number | null;

  recurrence_end_offset_days: number | null;

  workflow_id: string;
}

export class OutreachWorkflowStageWithWorkflow extends OutreachWorkflowStage {
  workflow: OutreachWorkflow;
}

export enum OutreachWorkflowRunStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export class OutreachWorkflowRun extends Model {
  id: string;

  @TransformStringToDayJS()
  baseline_ts: dayjs.Dayjs;

  call_id: string | null;

  email_id: string | null;

  partner_eval_id: string;

  reason: string | null;

  status: OutreachWorkflowRunStatus;

  @TransformStringToDayJS()
  scheduled_for: dayjs.Dayjs;

  stage_id: string;

  @Type(() => OutreachWorkflowStageWithWorkflow)
  stage: OutreachWorkflowStageWithWorkflow | null;
}

export class OutreachWorkflowStagePartnerEval extends Model {
  id: string;

  reason: string | null;

  status: OutreachWorkflowStagePartnerEvalStatus;

  partner_id: string;

  @Type(() => OutreachWorkflowStageWithWorkflow)
  stage: OutreachWorkflowStageWithWorkflow;

  @Type(() => OutreachWorkflowRun)
  run: OutreachWorkflowRun | null;

  @TransformStringToDayJS()
  last_evaluated_at: dayjs.Dayjs;

  parent_email_id: string | null;
}
